import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Paper } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { DialogConfirm, FieldWrapper, PageHeader, StepActions } from "@components/ui_v2";
import { ListPasswordRules, TextFieldPassword } from "@components/auth";
import { useUpdatePasswordChangeMutation } from "@api/onboarding";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { PASSWORD } from "@utils/expreciones";
import { PATHS_AUTH_V2 } from "@helpers/constantes";
import { ApiError } from "@interfaces/response";

/**
 * Nueva contraseña con el diseño v2: mismo servicio que `NuevaContrasena` (legacy). El enlace del
 * correo trae el id de la solicitud en `?request=`; sin él no se puede actualizar. Si la liga caducó (la respuesta
 * trae `errors.user_password_change_request`), se avisa y se ofrece pedir un enlace nuevo.
 */
export const NuevaContrasenaV2 = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const interfaceConfig = useInterfaceConfig();
  const [updatePasswordChange] = useUpdatePasswordChangeMutation();
  const [result, setResult] = useState<"" | "success" | "error" | "expired">("");

  const requestId = new URLSearchParams(search).get("request");

  const formik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: yup.object().shape({
      password: yup.string().required("Campo requerido").matches(PASSWORD, "Formato Incorrecto"),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref("password")], "Las contraseñas no coinciden")
        .required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!requestId) {
        setResult("error");
        return;
      }

      try {
        setSubmitting(true);
        await updatePasswordChange({
          request_id: requestId,
          password: values.password,
          company: interfaceConfig.name.toUpperCase(),
        }).unwrap();
        setResult("success");
      } catch (error) {
        const errors = (error as ApiError).data?.errors as Record<string, string> | undefined;
        if (errors?.user_password_change_request) {
          setResult("expired");
          return;
        }
        Sentry.captureException(error, { extra: { component: "NuevaContrasenaV2-update_password" } });
        setResult("error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box>
      <PageHeader
        eyebrow="Nueva contraseña"
        title="Crea tu nueva contraseña"
        subtitle="Escribe y confirma la contraseña con la que vas a iniciar sesión."
      />

      <Paper variant="lendia_paper">
        <Box
          component="form"
          noValidate
          onSubmit={formik.handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <FieldWrapper label="Nueva contraseña" error={(formik.touched.password && formik.errors.password) || ""}>
            <TextFieldPassword
              name="password"
              autoComplete="new-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting}
              testId="nueva-contrasena-password-input"
            />
          </FieldWrapper>
          <FieldWrapper
            label="Confirma tu contraseña"
            error={(formik.touched.confirmPassword && formik.errors.confirmPassword) || ""}
          >
            <TextFieldPassword
              name="confirmPassword"
              autoComplete="new-password"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting}
              testId="nueva-contrasena-confirm-input"
            />
          </FieldWrapper>
          <ListPasswordRules password={formik.values.password} />
        </Box>
      </Paper>

      <StepActions
        onBack={() => navigate(PATHS_AUTH_V2.login.path)}
        backLabel="Volver a iniciar sesión"
        onNext={formik.submitForm}
        nextLabel="Guardar contraseña"
        nextDisabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
        backTestId="nueva-contrasena-back-button"
        nextTestId="nueva-contrasena-submit-button"
      />

      <DialogConfirm
        open={result === "success"}
        tone="success"
        icon={<CheckCircleOutline />}
        title="¡Contraseña actualizada!"
        message="Tu contraseña ha sido actualizada exitosamente. Ya puedes iniciar sesión."
        confirmLabel="Cerrar"
        onConfirm={() => navigate(PATHS_AUTH_V2.login.path)}
        onClose={() => navigate(PATHS_AUTH_V2.login.path)}
        confirmTestId="nueva-contrasena-success-close-button"
      />
      <DialogConfirm
        open={result === "error"}
        tone="danger"
        icon={<ErrorOutline />}
        title="Error al actualizar"
        message="Hubo un problema al actualizar tu contraseña. Inténtalo de nuevo."
        confirmLabel="Cerrar"
        onConfirm={() => setResult("")}
        onClose={() => setResult("")}
        confirmTestId="nueva-contrasena-error-close-button"
      />
      <DialogConfirm
        open={result === "expired"}
        tone="danger"
        icon={<ErrorOutline />}
        title="El enlace expiró"
        message="El enlace para restablecer tu contraseña ha expirado por motivos de seguridad, por favor, solicita un nuevo enlace para continuar."
        confirmLabel="Solicitar un nuevo enlace"
        onConfirm={() => navigate(PATHS_AUTH_V2.recuperarContrasena.path)}
        onClose={() => setResult("")}
        confirmTestId="nueva-contrasena-expired-button"
      />
    </Box>
  );
};
